import type { ReactNode } from "react"
import { bufferOptions, serviceLevelOptions } from "~/models/enum"
import { getEffectiveBufferType } from "~/utils/metadataUtils"
import { SelectFieldRow } from "./SelectFieldRow"
import { NumericFieldRow } from "./NumericFieldRow"
import { ErpIndicator } from "./ErpIndicator"
import type { MetadataForEdit, IFormData } from "./types"

const serviceLevelSelectOptions = serviceLevelOptions.map((opt) => ({
  value: opt.zfactor,
  label: opt.label,
}))

type BufferFieldsGroupProps = {
  formData: IFormData
  setFormData: (data: IFormData) => void
  metadataForEdit: MetadataForEdit
  getFieldBadges: (fieldName: keyof IFormData) => ReactNode
  shouldShowResetButton: (fieldName: keyof IFormData) => boolean
  handleResetToDefault: (fieldName: keyof IFormData) => void
}

/**
 * Manages coupled bufferType/bufferValue fields for multi-SKU editing.
 *
 * STATE SEMANTICS:
 * - `undefined`: Field untouched (shows "Mixed values" if isMultiple)
 * - `null`: Field reset/cleared (shows company default)
 * - `value`: User selected specific value
 *
 * PLACEHOLDER RULES:
 * - "Mixed values" shown ONLY when: isMultiple && formData === undefined
 * - Company default shown when: formData is null OR not mixed
 *
 * CURRENT BUFFER TYPE LOGIC:
 * Falls back through: formData → field value (if not "Multiple values") → company default → "noSafetyStock"
 * This ensures the appropriate input field (Service Level or Week Cover) displays even after reset.
 */
export const BufferFieldsGroup = ({
  formData,
  setFormData,
  metadataForEdit,
  getFieldBadges,
  shouldShowResetButton,
  handleResetToDefault,
}: BufferFieldsGroupProps) => {
  const bufferTypeField = metadataForEdit.fields.bufferType
  const bufferValueField = metadataForEdit.fields.bufferValue

  const getBufferTypePlaceholder = () => {
    // Only show "Mixed values" if field hasn't been touched (undefined)
    if (bufferTypeField?.isMultiple && formData.bufferType === undefined) {
      return "Mixed values"
    } else if (formData.bufferType == null && bufferTypeField?.metadata?.defaultValue) {
      return (
        bufferOptions.find((opt) => opt.value === bufferTypeField.metadata?.defaultValue)?.label ??
        bufferTypeField.metadata.defaultValue
      )
    } else {
      return undefined
    }
  }

  const getBufferValuePlaceholder = () => {
    // Only show "Mixed values" if bufferType hasn't been touched (undefined)
    if (bufferValueField?.isMultiple && formData.bufferType === undefined) {
      return "Mixed values"
    } else if (formData.bufferValue == null && bufferValueField?.metadata?.defaultValue) {
      return (
        serviceLevelOptions.find((opt) => opt.zfactor === bufferValueField.metadata?.defaultValue)
          ?.label ?? bufferValueField.metadata.defaultValue
      )
    } else {
      return undefined
    }
  }

  const getResetTooltip = () => {
    if (bufferTypeField?.metadata?.defaultValue) {
      return `Reset to company default: ${
        bufferOptions.find((opt) => opt.value === bufferTypeField.metadata?.defaultValue)?.label ??
        bufferTypeField.metadata.defaultValue
      }`
    }
    return "Clear custom value"
  }

  const getWeekCoverPlaceholder = (): string | number | undefined => {
    // Only show "Mixed values" if bufferType hasn't been touched (undefined)
    if (bufferValueField?.isMultiple && formData.bufferType === undefined) {
      return "Mixed values"
    }
    const defaultValue = bufferValueField?.metadata?.defaultValue
    if (defaultValue !== undefined && defaultValue !== null) {
      return defaultValue
    }
    return undefined
  }

  const currentBufferType = getEffectiveBufferType(formData, metadataForEdit)

  const getBufferValueResetTooltip = () => {
    const defaultValue = bufferValueField?.metadata?.defaultValue
    if (defaultValue !== undefined && defaultValue !== null) {
      if (currentBufferType === "serviceLevel") {
        const label =
          serviceLevelOptions.find((opt) => opt.zfactor === defaultValue)?.label ?? defaultValue
        return `Reset to company default: ${label}`
      }
      if (currentBufferType === "weekCover") {
        return `Reset to company default: ${defaultValue} weeks`
      }
    }
    return "Clear custom value"
  }

  const bufferTypeErpIndicator = (
    <ErpIndicator
      erpValue={bufferTypeField?.metadata?.erpValue}
      erpValues={bufferTypeField?.metadata?.erpValues}
    />
  )

  const bufferValueErpIndicator = (
    <ErpIndicator
      erpValue={bufferValueField?.metadata?.erpValue}
      erpValues={bufferValueField?.metadata?.erpValues}
      unit={currentBufferType === "weekCover" ? "weeks" : undefined}
    />
  )

  return (
    <>
      <SelectFieldRow
        label="Safety stock type"
        value={
          formData.bufferType != null
            ? bufferOptions.find((b) => b.value === formData.bufferType)
            : null
        }
        onChange={(value) => {
          setFormData({
            ...formData,
            bufferType: value?.value ?? null,
            bufferValue: null,
          })
        }}
        options={bufferOptions}
        placeholder={getBufferTypePlaceholder()}
        isDisabled={!bufferTypeField?.isEditable}
        badges={getFieldBadges("bufferType")}
        erpIndicator={bufferTypeErpIndicator}
        showResetButton={shouldShowResetButton("bufferType")}
        resetTooltip={getResetTooltip()}
        onReset={() =>
          setFormData({
            ...formData,
            bufferType: null,
            bufferValue: null,
          })
        }
        isClearable
      />

      {currentBufferType === "serviceLevel" && (
        <SelectFieldRow
          label="Service level"
          value={
            formData.bufferValue != null
              ? serviceLevelSelectOptions.find((option) => option.value === formData.bufferValue)
              : null
          }
          onChange={(option) => setFormData({ ...formData, bufferValue: option?.value ?? null })}
          options={serviceLevelSelectOptions}
          placeholder={getBufferValuePlaceholder()}
          isDisabled={!bufferValueField?.isEditable}
          badges={getFieldBadges("bufferValue")}
          erpIndicator={bufferValueErpIndicator}
          showResetButton={shouldShowResetButton("bufferValue")}
          onReset={() => handleResetToDefault("bufferValue")}
          resetTooltip={getBufferValueResetTooltip()}
          isClearable
          isSearchable={false}
          menuPlacement="top"
        />
      )}

      {currentBufferType === "weekCover" && (
        <NumericFieldRow
          label="Amount of weeks"
          fieldName="bufferValue"
          formData={formData}
          setFormData={setFormData}
          metadataForEdit={metadataForEdit}
          getFieldBadges={getFieldBadges}
          shouldShowResetButton={shouldShowResetButton}
          handleResetToDefault={handleResetToDefault}
          unit="weeks"
          customPlaceholder={getWeekCoverPlaceholder()}
        />
      )}
    </>
  )
}
